import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Bookmark, ExternalLink } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { FolderComments } from "@/components/news/FolderComments";

// A folder someone passed on: read-only, no login, nothing to sign up for.
export default function SharedFolder() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, owner: "", folder: "", stories: [], error: "" });

  useEffect(() => {
    api
      .get(`/news/shared/${token}`)
      .then(({ data }) => setState({ loading: false, ...data, error: "" }))
      .catch((e) =>
        setState({
          loading: false, owner: "", folder: "", stories: [],
          error: apiError(e.response?.data?.detail) || "That link is no longer live",
        }));
  }, [token]);

  return (
    <div className="mx-auto max-w-2xl px-5 py-16 sm:px-8" data-testid="shared-folder-page">
      <p className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-brand">
        <Bookmark className="h-3.5 w-3.5" /> Shared reading
      </p>
      <h1 className="mt-3 font-display text-[1.7rem] font-extrabold tracking-tighter sm:text-[2.25rem]">
        {state.loading ? "Opening…"
          : state.error ? "Nothing here"
          : `${state.owner}'s “${state.folder}”`}
      </h1>

      {state.error && (
        <p className="mt-3 text-sm text-ink-soft" data-testid="shared-folder-error">{state.error}</p>
      )}

      {!state.loading && !state.error && state.stories.length === 0 && (
        <p className="mt-3 text-sm text-ink-soft">This folder is empty for now.</p>
      )}

      <div className="mt-6 space-y-3">
        {state.stories.map((story) => (
          <div key={story.story_id} className="rounded-2xl border border-white/10 bg-panel p-4"
               data-testid={`shared-story-${story.story_id}`}>
            <a href={story.url} target="_blank" rel="noreferrer"
               className="inline-flex items-start gap-2 text-base font-semibold hover:text-brand" dir="auto">
              {story.title}
              <ExternalLink className="mt-1 h-3.5 w-3.5 shrink-0 text-ink-soft" />
            </a>
            <p className="mt-1 text-xs text-ink-soft">
              {story.source}
              {story.read_min ? (
                <span className="text-brand">{` · ${story.read_est ? "~" : ""}${story.read_min} min read`}</span>
              ) : null}
            </p>
            {story.note && (
              <p className="mt-2 rounded-xl bg-white/5 px-3 py-2 text-sm italic text-ink-soft" dir="auto">
                “{story.note}”
              </p>
            )}
          </div>
        ))}
      </div>

      {!state.loading && !state.error && <FolderComments token={token} canPost />}

      <p className="mt-10 text-xs text-ink-soft">
        Headlines come from publishers — every link opens the original article.
      </p>
    </div>
  );
}
